import { Component, Inject, Input, OnInit } from '@angular/core';
import { AgrDatePipe, LocaleService } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import * as Highcharts from 'highcharts';
import { Flock, WeekRegistrationKPIItem } from 'src/app/flock/models/flock.model';

@Component({
  selector: 'app-flock-multiline-chart',
  templateUrl: './flock-multiline-chart.component.html',
  standalone: false,
})
export class FlockMultilineChartComponent implements OnInit {
  @Input() flockData: DataSeries<number, number>;
  @Input() breedersReferenceBenchmarks: DataSeries<number, number>;
  @Input() porphyrioBenchmarks: DataSeries<number, number>;
  @Input() otherFlockData: DataSeries<number, number>;
  @Input() otherFlockName: string;
  @Input() kpi: WeekRegistrationKPIItem;
  @Input() flock: Flock;

  chartOptions: Highcharts.Options;
  chartSeries: Highcharts.SeriesOptionsType[];

  constructor(
    @Inject(LocaleService) private localeService: LocaleService,
    private agrDate: AgrDatePipe,
  ) {}

  ngOnInit(): void {
    this.createChart();
  }

  private getInitialOptions(): Highcharts.Options {
    const withAutoScale = (bounds: { min?: number; max?: number }) => ({
      name: '',
      ...(this.hasDataOutsideBounds(bounds) ? {} : bounds),
    });
    const getYaxisOptions = () => {
      switch (this.kpi.name) {
        // FeedConversionRateCumulative has a y-axis range 1 - 3
        case 'FeedConversionRateCumulative':
          return withAutoScale({ min: 1, max: 3 });
        // AverageEggWeight has a y-axis range 30 - 75
        case 'AverageEggWeight':
          return withAutoScale({ min: 30, max: 75 });
        // RateOfLay have a max of 100%
        case 'RateOfLay':
          return withAutoScale({ max: 100 });
        case 'FeedToEggRatio':
          return withAutoScale({ min: 100, max: 200 });
        case 'FeedConversionRate':
          return withAutoScale({ min: 1.7, max: 3.0 });
      }
    };
    return {
      chart: {
        height: '450px',
        zoomType: 'xy',
        panning: {
          enabled: true,
          type: 'xy',
        },
        panKey: 'shift',
      },
      xAxis: {
        title: {
          enabled: true,
          text: this.localeService.translate(
            'flock-overview.week-registrations.benchmark.axis.week',
          ),
        },
      },
      yAxis: {
        ...getYaxisOptions(),
        title: {
          text: this.localeService.translate(
            'flock-overview.week-registrations.benchmark.axis.values',
          ),
        },
      },
      tooltip: {
        shared: true,
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: true,
          },
        },
      },
      credits: {
        enabled: false,
      },
    } as Highcharts.Options;
  }

  private hasDataOutsideBounds(bounds: { min?: number; max?: number }): boolean {
    return [
      this.flockData,
      this.breedersReferenceBenchmarks,
      this.porphyrioBenchmarks,
      this.otherFlockData,
    ].some((dataSeries) =>
      Object.values(dataSeries?.series ?? {}).some((series) =>
        series.some((serie) =>
          serie.values.some(
            ({ value }) =>
              Number.isFinite(value) &&
              ((bounds.min !== undefined && value < bounds.min) ||
                (bounds.max !== undefined && value > bounds.max)),
          ),
        ),
      ),
    );
  }

  private createChart(): void {
    this.chartSeries = [];
    this.chartOptions = this.getInitialOptions();
    let start = 9999;
    let end = 0;
    const weeks: number[] = [];

    const kpiName = this.getKpiName(this.kpi.name);

    // Set data.
    const flockSerieName = this.localeService.translate(
      'flock-overview.week-registrations.filters.chart-lines.current-flock',
    );
    Object.keys(this.flockData?.series || []).forEach((flockId) => {
      this.flockData.series[flockId].forEach((serie) => {
        if (serie.start < start) {
          start = serie.start;
        }
        if (serie.end > end) {
          end = serie.end;
        }

        serie.values.forEach((v) => {
          if (!weeks.includes(v.dimensionValue)) {
            weeks.push(v.dimensionValue);
          }
        });

        // Add flock data values serie.
        this.chartSeries.push({
          type: 'line',
          name: `${flockSerieName} - ${this.getKpiName(serie.parameterCode)}`,
          data: serie.values
            .filter((c) => !this.isNaN(c.value))
            .map((c) => [c.dimensionValue, c.value]),
        });
      });
    });

    const otherFlockSerieName = `${this.otherFlockName} (${this.localeService.translate(
      'flock-overview.week-registrations.filters.chart-lines.other-flock-lower-case',
    )})`;
    Object.keys(this.otherFlockData?.series || []).forEach((flockId) => {
      this.otherFlockData.series[flockId].forEach((serie) => {
        if (serie.start < start) {
          start = serie.start;
        }
        if (serie.end > end) {
          end = serie.end;
        }

        serie.values.forEach((v) => {
          if (!weeks.includes(v.dimensionValue)) {
            weeks.push(v.dimensionValue);
          }
        });

        // Add other flock data values serie.
        this.chartSeries.push({
          type: 'line',
          name: `${otherFlockSerieName} - ${kpiName}`,
          data: serie.values
            .filter((c) => !this.isNaN(c.value))
            .map((c) => [c.dimensionValue, c.value]),
        });
      });
    });

    if (
      this.breedersReferenceBenchmarks !== null &&
      this.breedersReferenceBenchmarks !== undefined
    ) {
      const serieName = this.localeService.translate(
        'flock-overview.week-registrations.benchmark.breedersReference',
      );
      const breedName = this.flock?.arrival?.breed?.name || '';
      const breedersReferenceBenchmarkKeys = Object.keys(this.breedersReferenceBenchmarks.series);
      breedersReferenceBenchmarkKeys.forEach((breedersReferenceBenchmarkKey) => {
        this.breedersReferenceBenchmarks.series[breedersReferenceBenchmarkKey].forEach((serie) => {
          // Add values serie.
          this.chartSeries.push({
            type: 'line',
            dashStyle: 'Dash',
            name: `${serieName} (${breedName}) - ${kpiName}`,
            data: serie.values
              .filter((c) => c.dimensionValue <= end)
              .map((c) => [
                c.dimensionValue,
                this.kpi?.decimalsPrecision
                  ? this.round(c.value, this.kpi?.decimalsPrecision)
                  : c.value,
              ]),
          });
        });
      });
    }

    if (this.porphyrioBenchmarks !== null && this.porphyrioBenchmarks !== undefined) {
      const serieName = this.localeService.translate(
        'flock-overview.week-registrations.benchmark.porphyrioBenchmark',
      );
      const updated = this.porphyrioBenchmarks.metaData?.other?.Updated;
      const updatedLabel = updated
        ? ` (${this.agrDate.transform(<Date>(<unknown>updated), 'shortDate')})`
        : '';

      const porphyrioBenchmarkKeys = Object.keys(this.porphyrioBenchmarks.series);
      porphyrioBenchmarkKeys.forEach((porphyrioBenchmarkKey) => {
        const me = this;
        this.porphyrioBenchmarks.series[porphyrioBenchmarkKey].forEach((serie) => {
          // Add values serie.
          this.chartSeries.push({
            type: 'line',
            dashStyle: 'Dash',
            tooltip: {
              pointFormatter() {
                const highChartsPoint: Highcharts.Point = this;
                return `<span style="color: ${
                  highChartsPoint.color
                }">\u25CF</span> ${me.localeService.translate(
                  'flock-overview.week-registrations.benchmark.porphyrioBenchmark',
                  // @ts-ignore
                )} (${highChartsPoint.properties.flocksUsed} ${me.localeService.translate(
                  'flock-overview.week-registrations.benchmark.flocks',
                )}) - ${serieName}: <b>${this.y}</b>`;
              },
            },
            name: `${serieName} - ${kpiName}${updatedLabel}`,
            data: serie.values
              .filter((c) => c.dimensionValue <= end)
              .map((c) => ({
                x: c.dimensionValue,
                y: this.kpi?.decimalsPrecision
                  ? this.round(c.value, this.kpi?.decimalsPrecision)
                  : c.value,
                properties: { flocksUsed: c.properties?.['FlocksUsed'] },
              })),
          });
        });
      });
    }
  }

  round(decimal: number, decimalPoints: number): number {
    return Math.round(decimal * Math.pow(10, decimalPoints)) / Math.pow(10, decimalPoints);
  }

  isNaN(value: number): boolean {
    return Number.isNaN(value) || !Number.isFinite(value);
  }

  private getKpiName(parameterCode: string): string {
    const translationKeyPrefix = this.kpi.translationKey?.substring(
      0,
      this.kpi.translationKey.lastIndexOf('.') + 1,
    );
    return this.localeService.translate(
      translationKeyPrefix
        ? `${translationKeyPrefix}${parameterCode}`
        : `flock-overview.week-registrations.kpis.${parameterCode}`,
    );
  }
}
